import produce from "immer";
import { forEach, head, keys } from "lodash";
import { AbstractDate, Interval, PeriodRange } from "@linkfluence/models/date";
import { Facet } from "@linkfluence/models/facet";
import { Locale } from "@linkfluence/models/locale";
import { Platform } from "@linkfluence/models/platform";
import { Timezone } from "@linkfluence/models/timezone";
import {
  getIntervalFromDates,
  getLastYearPeriod,
  getPreviousPeriod,
} from "@tootools/date";
import {
  getDateFromClause,
  getInnerClauseFromClause,
} from "@radarly/models/clause/getter";
import { mergeClauses } from "@radarly/models/clause/merge";
import { refineClauseWithDate } from "@radarly/models/clause/refiner";
import { InsightPage } from "@radarly/models/insight-pages/model";
import { isCardRow } from "@radarly/models/insight-pages/row";
import {
  AIFragment,
  SearchFragment,
  isSearchFragmentDateMetrics,
  isSearchFragmentDateTerm,
  isSearchFragmentTerm,
} from "@radarly/models/search";
import { isCardWithRequest, isTimeUnitCard } from "./cards/is";
import { Clause } from "./clause";
import { getComparaisonPeriodFromFragments } from "./fragment";

export interface PublicPageCtxFocuse {
  id: number;
  label: string;
  color: string;
}

export interface PublicPageCtxRecoImage {
  id: number;
  label: string;
  image: string;
}

export interface PublicPageProductsCtx {
  id: number;
  label: string;
  image: string;
}

export interface PublicPageCorpus {
  id: number;
  name: string;
  projectId: number;
  created: Date;
}

export interface PublicProductCtx {
  [Facet.Product]?: PublicPageProductsCtx[];
}

export interface PublicPageCtxWorkspace {
  color: string;
  created: string;
  id: number;
  isCustomPageMenuActivated: boolean;
  isSocialWallActivated: boolean;
  label: string;
  pictureId: number;
  projectId: number;
  queryLength: number;
  updated: string;
}

export interface PublicPageCtx {
  locale: Locale;
  dashboard: PublicPageCtxWorkspace;
  userTags: [number, string][];
  customFields: unknown; // @TODO
  corpora: PublicPageCorpus[];
  focuses: PublicPageCtxFocuse[];
  platforms: Platform[];
  brandLogos: PublicPageCtxRecoImage[];
  personIdentifications: PublicPageCtxRecoImage[];
  occupations: unknown; // @TODO
  geos: unknown; // @TODO
  generatedOn: number;
  hasCredentials: {
    facebook: boolean;
    instagram: boolean;
  };
  filters: Clause;
  products: PublicPageProductsCtx[];
}

export interface PublicPage {
  customPage: InsightPage;
  ctx: PublicPageCtx;
}

export function generatePublicPage(
  page: InsightPage,
  options: Partial<{
    title: string;
    clause?: Clause;
    period?: PeriodRange;
  }>
) {
  return produce(page, (draft) => {
    if (options.title) {
      draft.title = options.title;
    }
    if (options.clause || options.period) {
      forEach(draft.rows, (row) => {
        if (isCardRow(row)) {
          forEach(row.cards, (card) => {
            if (card && isCardWithRequest(card)) {
              const comparaison = getComparaisonPeriodFromFragments(
                card.fragments[0],
                card.fragments[1] || card.fragments[0]
              );
              if (card.fragments[0]) {
                card.fragments[0] = getUpdatedFragment(
                  card.fragments[0],
                  {
                    timezone: page.timezone,
                    clause: options.clause,
                    period: options.period,
                  },
                  isTimeUnitCard(card) ? card.dataviz.timeUnit : undefined
                );
              }
              if (card.fragments[1] && comparaison) {
                card.fragments[1] = getUpdatedFragment(
                  card.fragments[1],
                  {
                    timezone: page.timezone,
                    clause: options.clause,
                    period: options.period
                      ? (getPreviousPeriodFromComparaison(
                          comparaison,
                          options.period?.from,
                          options.period?.to
                        ) as PeriodRange)
                      : null,
                  },
                  isTimeUnitCard(card) ? card.dataviz.timeUnit : undefined
                );
              }
            }
          });
        }
      });
    }
  });
}

function getPreviousPeriodFromComparaison(
  comparaison: "previous_period" | "last_year",
  from: AbstractDate,
  to: AbstractDate
) {
  if (from && to && comparaison) {
    return comparaison === "previous_period"
      ? getPreviousPeriod({ from, to })
      : getLastYearPeriod({ from, to });
  } else {
    return {
      from: null,
      to: null,
    };
  }
}

export function getUpdatedFragment<T extends SearchFragment | AIFragment>(
  fragment: T,
  options: Partial<{
    timezone: Timezone;
    clause?: Clause;
    period?: PeriodRange;
  }>,
  timeUnit: "default" | Interval.Week | Interval.Month
) {
  if (!fragment) {
    return undefined;
  }
  return produce(fragment, (draft) => {
    if (options.timezone) {
      draft.request.timezone = options.timezone;
    }
    draft.request.query = getClauseWithDate(
      getMergedClause(fragment.request.query, options.clause),
      options.period
    );
    if (isSearchFragmentDateTerm(draft) || isSearchFragmentDateMetrics(draft)) {
      const { from: fromClause, to: toClause } = getDateFromClause(
        draft.request.query
      );
      draft.request.aggs.date.interval =
        !!timeUnit && timeUnit !== "default"
          ? timeUnit
          : getIntervalFromDates(fromClause, toClause, "UTC");
    }
    // @ts-ignore
    if (draft.request.aggs) {
      // @ts-ignore
      const term = head(keys(draft.request.aggs));
      if (term) {
        // @ts-ignore
        const metric = head(draft.request.aggs[term]?.metrics);
        if (metric) {
          // @ts-ignore
          draft.request.aggs.metrics = { metrics: [metric] };
          if (isSearchFragmentTerm(draft)) {
            const innerClause = getInnerClauseFromClause(
              draft.request.query,
              Facet.Focuses
            );
            if (innerClause?.focuses && innerClause?.focuses?.length > 0) {
              // @ts-ignore
              if (draft.request.aggs[Facet.Focuses]) {
                // @ts-ignore
                draft.request.aggs[Facet.Focuses].focuses = innerClause.focuses;
              }
            }
          } else if (isSearchFragmentDateTerm(draft)) {
            const innerClause = getInnerClauseFromClause(
              draft.request.query,
              Facet.Focuses
            );
            if (innerClause?.focuses && innerClause?.focuses?.length > 0) {
              if (draft.request.aggs.date.aggs[Facet.Focuses]) {
                draft.request.aggs.date.aggs[Facet.Focuses].focuses =
                  innerClause.focuses;
              }
            }
          }
        }
      }
    }
  }) as T;
}

function getMergedClause(clause: Clause, filteredClause: Clause) {
  if (!filteredClause) {
    return clause;
  }
  return mergeClauses(clause, filteredClause);
}

function getClauseWithDate(clause: Clause, period: PeriodRange) {
  if (!period) {
    return clause;
  }
  return refineClauseWithDate(clause, period);
}
